/**
 * Panorama — indicadores de valor para el cliente final (REQ-MINT-006 §1).
 *
 * Solo se muestran indicadores de catálogo, modelos nuevos, rendimiento
 * general de modelos (media de ámbitos con dato), deprecaciones (lista) y
 * noticias. Sin salud de fuentes, colas de curación, mediciones internas,
 * variaciones internas ni rendimiento de plataforma.
 *
 * Las cards siguen el patrón del dashboard global: círculo gris + icono azul.
 */

import { useMemo, type CSSProperties } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  BarChart3,
  CalendarClock,
  Layers,
  Newspaper,
  Sparkles,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button, Card, CardContent, Spinner } from "@/components/ui";
import { useI18n } from "@/i18n";
import PanoramaFilters from "../components/PanoramaFilters";
import {
  useModelIntelDetections,
  useModelIntelModels,
  useModelIntelNews,
} from "../hooks/useModelIntel";
import {
  deriveOverallPerformance,
  derivePanoramaKpis,
  deriveSkillMatrix,
  deriveSkillRanking,
  SKILLS,
  type SkillKey,
} from "../lib/panorama";
import {
  applyPanoramaFilters,
  DEFAULT_PANORAMA_FILTERS,
  deriveEloPerUsd,
  derivePanoramaFilterOptions,
  derivePanoramaRangeCounts,
  parsePanoramaFilters,
  serializePanoramaFilters,
  type PanoramaFilters as PanoramaFiltersState,
} from "../lib/panoramaFilters";
import { formatDate } from "../lib/pricing";
import type { ModelListResponse } from "../types";

function LMArenaLink() {
  const { t } = useI18n();
  return (
    <a
      href="https://lmarena.ai"
      target="_blank"
      rel="noreferrer noopener"
      className="underline underline-offset-2 hover:text-foreground"
    >
      {t("modelIntel.panorama.sourceName")}
    </a>
  );
}

function SourceFooter({ testId }: { testId: string }) {
  const { t } = useI18n();
  return (
    <p
      className="text-[11px] text-muted-foreground"
      data-testid={testId}
    >
      {t("modelIntel.panorama.sourceLabel")} <LMArenaLink />
    </p>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  testId,
  to,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  testId: string;
  to?: string;
}) {
  const card = (
    <Card className={to ? "transition-colors hover:bg-accent/40" : undefined}>
      <CardContent className="flex items-center justify-between p-6">
        <div className="space-y-1">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p
            className="text-2xl font-bold tabular-nums text-foreground"
            data-testid={testId}
          >
            {value}
          </p>
        </div>
        <div className="rounded-full bg-muted p-3">
          <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
        </div>
      </CardContent>
    </Card>
  );
  if (!to) return card;
  return (
    <Link to={to} className="block" data-testid={`${testId}-link`}>
      {card}
    </Link>
  );
}

export function OverallPerformanceChart({
  data,
  total,
}: {
  data: Array<{ name: string; score: number; unit: "elo" | "percentile"; domains: number }>;
  total: number;
}) {
  const { t } = useI18n();
  const isElo = data[0]?.unit === "elo";
  const chartHeight = Math.max(320, Math.min(1200, data.length * 28));
  return (
    <Card className="h-full">
      <CardContent className="space-y-2 p-4" data-testid="panorama-overall">
        <div className="flex items-center gap-2 text-sm font-medium">
          <BarChart3 className="h-4 w-4 text-primary" aria-hidden="true" />
          <span data-testid="panorama-overall-title">
            {t("modelIntel.panorama.overallTitle")}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("modelIntel.panorama.overallSubtitle", {
            shown: data.length,
            total,
          })}
        </p>
        {data.length === 0 ? (
          <div className="space-y-1" data-testid="panorama-overall-empty">
            <p className="text-sm text-muted-foreground">
              {t("modelIntel.panorama.overallEmpty")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("modelIntel.panorama.overallEmptyHint")}
            </p>
          </div>
        ) : (
          <>
            <div className="max-h-[520px] overflow-y-auto">
              <div style={{ height: chartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data}
                    margin={{ left: 0, right: 8, top: 20, bottom: 90 }}
                  >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    interval={0}
                    angle={-35}
                    textAnchor="end"
                    height={90}
                    tick={{ fontSize: 10 }}
                  />
                  <YAxis
                    type="number"
                    domain={isElo ? ["dataMin - 10", "dataMax + 10"] : [0, 100]}
                    unit={isElo ? undefined : "%"}
                    width={52}
                    tick={{ fontSize: 10 }}
                  />
                  <Tooltip
                    formatter={(value: unknown) => [
                      isElo ? String(value) : `${String(value)}%`,
                      isElo ? "LMArena Elo" : "Score",
                    ]}
                  />
                  <Bar dataKey="score" fill="#8884d8" radius={[4, 4, 0, 0]}>
                    <LabelList
                      dataKey="score"
                      position="top"
                      formatter={(value: unknown) =>
                        isElo ? String(value) : `${String(value)}%`
                      }
                      fontSize={10}
                    />
                  </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            {data.length < 3 && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="panorama-overall-insufficient"
              >
                {t("modelIntel.panorama.overallInsufficient", {
                  count: data.length,
                })}
              </p>
            )}
          </>
        )}
        <SourceFooter testId="panorama-overall-source" />
      </CardContent>
    </Card>
  );
}


function skillCellStyle(
  score: number | null,
  min: number,
  max: number,
): CSSProperties | undefined {
  if (score === null) return undefined;
  const range = max - min;
  const ratio = range > 0 ? (score - min) / range : 0.5;
  const alpha = 0.08 + Math.max(0, Math.min(1, ratio)) * 0.42;
  return { backgroundColor: `rgba(136, 132, 216, ${alpha})` };
}

function SkillRankingCard({
  data,
  skill,
  limit,
  onSkillChange,
}: {
  data?: ModelListResponse;
  skill: SkillKey;
  limit: number;
  onSkillChange: (skill: SkillKey) => void;
}) {
  const { t } = useI18n();
  const ranking = useMemo(
    () => deriveSkillRanking(data, skill, limit === 0 ? 100 : limit),
    [data, skill, limit],
  );
  const rankingIsElo = ranking.some((row) => row.score > 100);
  const chartHeight = Math.max(240, Math.min(720, ranking.length * 28));
  const skillLabel = (key: SkillKey) =>
    t(
      `modelIntel.panorama.skills.${
        key === "codigo"
          ? "code"
          : key === "conversacion"
            ? "conversation"
            : "reasoning"
      }`,
    );

  return (
    <Card className="h-full">
      <CardContent className="space-y-2 p-4" data-testid="panorama-skills">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Layers className="h-4 w-4 text-primary" aria-hidden="true" />
          <span data-testid="panorama-skills-title">
            {t("modelIntel.panorama.skillsTitle")}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("modelIntel.panorama.skillsSubtitle")}
        </p>
        <div className="flex flex-wrap gap-1 pt-1">
          {SKILLS.map((item) => (
            <Button
              key={item.key}
              type="button"
              size="sm"
              variant={skill === item.key ? "default" : "outline"}
              onClick={() => onSkillChange(item.key)}
              data-testid={`skill-tab-${item.key}`}
              aria-pressed={skill === item.key}
            >
              {skillLabel(item.key)}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {t("modelIntel.panorama.skillRankingSubtitle", {
            skill: skillLabel(skill),
          })}
        </p>
        {ranking.length === 0 ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="panorama-skill-ranking-empty"
          >
            {t("modelIntel.panorama.overallEmpty")}
          </p>
        ) : (
          <div className="max-h-[420px] overflow-y-auto" data-testid="panorama-skill-ranking">
            <div style={{ height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={ranking}
                layout="vertical"
                margin={{ left: 8, right: 40, top: 4, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  domain={rankingIsElo ? ["dataMin - 10", "dataMax + 10"] : [0, 100]}
                  unit={rankingIsElo ? undefined : "%"}
                />
                <YAxis
                  type="category"
                  dataKey="modelRef"
                  width={160}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  formatter={(value: unknown) => [
                    rankingIsElo ? String(value) : `${String(value)}%`,
                    rankingIsElo ? "LMArena Elo" : skillLabel(skill),
                  ]}
                />
                <Bar dataKey="score" fill="#8884d8" radius={[0, 4, 4, 0]}>
                  <LabelList
                    dataKey="score"
                    position="right"
                    formatter={(value: unknown) =>
                      rankingIsElo ? String(value) : `${String(value)}%`
                    }
                    fontSize={10}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
          </div>
        )}
        <SourceFooter testId="panorama-skills-source" />
      </CardContent>
    </Card>
  );
}

function SkillMapCard({ data, limit }: { data?: ModelListResponse; limit: number }) {
  const { t } = useI18n();
  const matrix = useMemo(
    () => deriveSkillMatrix(data, limit === 0 ? 100 : limit),
    [data, limit],
  );
  const scores = matrix.flatMap((row) =>
    SKILLS.map((item) => row.skills[item.key]).filter(
      (score): score is number => score !== null,
    ),
  );
  const minScore = scores.length > 0 ? Math.min(...scores) : 0;
  const maxScore = scores.length > 0 ? Math.max(...scores) : 1;
  const skillLabel = (key: SkillKey) =>
    t(
      `modelIntel.panorama.skills.${
        key === "codigo"
          ? "code"
          : key === "conversacion"
            ? "conversation"
            : "reasoning"
      }`,
    );

  return (
    <Card>
      <CardContent className="space-y-2 p-4" data-testid="panorama-skill-map">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Layers className="h-4 w-4 text-primary" aria-hidden="true" />
          <span data-testid="panorama-skill-map-title">
            {t("modelIntel.panorama.skillMapTitle")}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("modelIntel.panorama.skillMapSubtitle", { count: matrix.length })}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3 font-medium">
                  {t("modelIntel.panorama.skillModel")}
                </th>
                {SKILLS.map((item) => (
                  <th key={item.key} className="px-2 py-2 text-center font-medium">
                    {skillLabel(item.key)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => (
                <tr key={row.modelRef} className="border-b border-border/60">
                  <td className="py-2 pr-3">
                    <Link
                      to={`/model-intel/model/${encodeURIComponent(row.modelRef)}`}
                      className="font-medium hover:underline"
                    >
                      {row.modelRef}
                    </Link>
                    <span className="ml-2 text-[11px] text-muted-foreground">
                      {row.provider}
                    </span>
                  </td>
                  {SKILLS.map((item) => {
                    const score = row.skills[item.key];
                    return (
                      <td
                        key={item.key}
                        className="px-1 py-1 text-center"
                        data-testid={`skill-cell-${row.modelRef}-${item.key}`}
                        title={
                          score === null
                            ? t("modelIntel.panorama.skillNoData")
                            : `${skillLabel(item.key)}: ${score.toFixed(1)}`
                        }
                      >
                        <span
                          className="inline-flex min-w-16 justify-center rounded px-2 py-1 tabular-nums"
                          style={skillCellStyle(score, minScore, maxScore)}
                        >
                          {score === null
                            ? t("modelIntel.panorama.skillNoData")
                            : score.toFixed(1)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <SourceFooter testId="panorama-skill-map-source" />
      </CardContent>
    </Card>
  );
}

export default function PanoramaPage() {
  const { t } = useI18n();
  const models = useModelIntelModels();
  const news = useModelIntelNews(30);
  const detections = useModelIntelDetections("new");

  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parsePanoramaFilters(searchParams), [searchParams]);
  const filtered = useMemo(
    () => applyPanoramaFilters(models.data, filters),
    [models.data, filters],
  );
  const filterOptions = useMemo(
    () => derivePanoramaFilterOptions(models.data),
    [models.data],
  );
  const rangeCounts = useMemo(
    () => derivePanoramaRangeCounts(models.data, filters.skill),
    [models.data, filters.skill],
  );

  const kpis = useMemo(() => derivePanoramaKpis(models.data), [models.data]);
  const allOverall = useMemo(
    () => deriveOverallPerformance(models.data),
    [models.data],
  );
  const overallAll = useMemo(() => {
    const rows = deriveOverallPerformance(filtered.data);
    if (filters.sort === "elo_asc") return [...rows].reverse();
    if (filters.sort === "name") {
      return [...rows].sort((a, b) => a.modelRef.localeCompare(b.modelRef));
    }
    if (filters.sort === "value_desc") {
      const value = deriveEloPerUsd(filtered.data);
      return [...rows].sort(
        (a, b) => (value.get(b.modelRef) ?? -Infinity) - (value.get(a.modelRef) ?? -Infinity),
      );
    }
    return rows;
  }, [filtered.data, filters.sort]);
  const overall = useMemo(() => {
    const limit = filters.limit === 0 ? overallAll.length : filters.limit;
    return overallAll.slice(0, limit).map((row) => ({
      name: row.modelRef,
      score: row.score,
      unit: row.unit,
      domains: row.domains,
    }));
  }, [filters.limit, overallAll]);

  const updateFilters = (next: PanoramaFiltersState) => {
    setSearchParams(serializePanoramaFilters(next), { replace: true });
  };
  const resetFilters = () => {
    setSearchParams(serializePanoramaFilters(DEFAULT_PANORAMA_FILTERS), {
      replace: true,
    });
  };
  const isLoading = models.isLoading;
  const isError = models.isError;

  return (
    <div className="space-y-6 p-6" data-testid="model-intel-panorama">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" aria-hidden="true" />
          <div>
            <h1 className="text-2xl font-semibold">
              {t("modelIntel.panorama.title")}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("modelIntel.panorama.subtitle")}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void models.refetch()}
          disabled={models.isFetching}
        >
          {t("modelIntel.panorama.refresh")}
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      )}
      {isError && !isLoading && (
        <div className="rounded-md border p-4 text-sm" role="alert">
          {t("modelIntel.panorama.error")}
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <KpiCard
              icon={Layers}
              label={t("modelIntel.panorama.cards.offerings")}
              value={String(kpis.offerings)}
              testId="kpi-offerings"
            />
            <KpiCard
              icon={Layers}
              label={t("modelIntel.panorama.cards.uniqueModels")}
              value={String(kpis.uniqueModels)}
              testId="kpi-unique-models"
            />
            <KpiCard
              icon={Sparkles}
              label={t("modelIntel.panorama.cards.newModels")}
              value={
                detections.isError
                  ? t("modelIntel.panorama.empty")
                  : detections.isLoading
                    ? "…"
                    : String(detections.data?.total ?? 0)
              }
              testId="kpi-new-models"
            />
          </div>

          <PanoramaFilters
            filters={filters}
            options={filterOptions}
            rangeCounts={rangeCounts}
            matchedModels={overallAll.length}
            totalModels={allOverall.length}
            priceVisible={models.data?.price_visibility === "included"}
            onChange={updateFilters}
            onReset={resetFilters}
          />

          <div className="grid gap-4 lg:grid-cols-2">
            <OverallPerformanceChart data={overall} total={overallAll.length} />
            <SkillRankingCard
              data={filtered.data}
              skill={filters.skill === "general" ? "codigo" : filters.skill}
              limit={filters.limit}
              onSkillChange={(skill) => updateFilters({ ...filters, skill })}
            />
          </div>

          <SkillMapCard data={filtered.data} limit={filters.limit} />

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CalendarClock className="h-4 w-4 text-primary" aria-hidden="true" />
                  {t("modelIntel.panorama.deprecatedTitle")}
                </div>
                {kpis.deprecated.length === 0 ? (
                  <p
                    className="text-sm text-gray-500"
                    data-testid="deprecated-empty"
                  >
                    {t("modelIntel.panorama.empty")}
                  </p>
                ) : (
                  <ul
                    className="space-y-1 text-sm"
                    data-testid="deprecated-list"
                  >
                    {kpis.deprecated.slice(0, 8).map((row) => (
                      <li
                        key={row.key}
                        className="flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{row.label}</span>
                        <span className="shrink-0 capitalize text-gray-500">
                          {row.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-2 p-4" data-testid="panorama-news">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Newspaper className="h-4 w-4 text-primary" aria-hidden="true" />
                  {t("modelIntel.panorama.newsTitle")}
                </div>
                {news.isError || (news.data?.items ?? []).length === 0 ? (
                  <p
                    className="text-sm text-gray-500 dark:text-gray-400"
                    data-testid="panorama-news-empty"
                  >
                    {t("modelIntel.panorama.newsEmpty")}
                  </p>
                ) : (
                  <ul
                    className="space-y-1.5 text-sm"
                    data-testid="panorama-news-list"
                  >
                    {(news.data?.items ?? []).slice(0, 6).map((item) => (
                      <li
                        key={`${item.source}-${item.published_at ?? item.url ?? ""}`}
                      >
                        <a
                          href={item.url ?? undefined}
                          className="underline"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {item.title}
                        </a>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t("modelIntel.card.newsSource", {
                            source: item.source,
                            date: formatDate(item.published_at),
                          })}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <p
            className="text-xs text-gray-500 dark:text-gray-400"
            data-testid="panorama-provenance"
          >
            {t("modelIntel.panorama.sourceLabel")} <LMArenaLink />
            {" · "}
            {t("modelIntel.panorama.provenanceCatalog", {
              catalogDate: models.data?.generated_at
                ? formatDate(models.data.generated_at)
                : "—",
            })}
          </p>
        </>
      )}
    </div>
  );
}
